import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Phone, User, Clock, MessageSquare, CheckCircle, AlertCircle, Circle, Plus, Filter, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TasksPage({ user, logout }) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'all', priority: 'all', type: 'all' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTask, setNewTask] = useState({ type: 'callback', phone: '', customer_name: '', message: '', priority: 'medium' });
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.status !== 'all') params.status = filter.status;
      if (filter.priority !== 'all') params.priority = filter.priority;
      if (filter.type !== 'all') params.task_type = filter.type;

      const [tasksRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tasks`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/tasks/stats`, { withCredentials: true })
      ]);

      setTasks(tasksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const updateTask = async (taskId, updates) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}`, null, {
        params: updates,
        withCredentials: true
      });
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const createTask = async () => {
    try {
      await axios.post(`${API_URL}/api/tasks`, null, {
        params: newTask,
        withCredentials: true
      });
      setShowCreateModal(false);
      setNewTask({ type: 'callback', phone: '', customer_name: '', message: '', priority: 'medium' });
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-blue-600" />;
      case 'pending': return <Circle className="w-4 h-4 text-yellow-600" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'callback': return '📞 Callback Request';
      case 'whatsapp_followup': return '💬 WhatsApp Follow-up';
      case 'scheduled_callback': return '⏰ Scheduled Callback';
      default: return type;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="p-6 space-y-6" data-testid="tasks-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-700">{stats.total || 0}</p>
            <p className="text-sm text-blue-600">Total Tasks</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-700">{stats.pending || 0}</p>
            <p className="text-sm text-yellow-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-indigo-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-indigo-700">{stats.in_progress || 0}</p>
            <p className="text-sm text-indigo-600">In Progress</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-700">{stats.completed || 0}</p>
            <p className="text-sm text-green-600">Completed</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-700">{stats.high_priority || 0}</p>
            <p className="text-sm text-red-600">High Priority</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filter.priority} onValueChange={(v) => setFilter({ ...filter, priority: v })}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v })}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="callback">Callback</SelectItem>
              <SelectItem value="whatsapp_followup">WhatsApp Follow-up</SelectItem>
              <SelectItem value="scheduled_callback">Scheduled Callback</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={fetchTasks} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-10 text-gray-500">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
            <p>No tasks found. Great job! 🎉</p>
          </div>
        ) : (
          tasks.map((task) => (
            <Card 
              key={task.task_id} 
              className="hover:shadow-md transition-shadow cursor-pointer" 
              data-testid={`task-${task.task_id}`}
              onClick={() => handleTaskClick(task)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getStatusIcon(task.status)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{getTypeLabel(task.type)}</span>
                      <Badge className={getPriorityColor(task.priority)}>
                        {task.priority}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {task.customer_name || 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {task.phone}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(task.created_at)}
                      </span>
                    </div>
                    
                    {task.message && (
                      <p className="text-sm text-gray-500 flex items-start gap-1">
                        <MessageSquare className="w-3 h-3 mt-1 flex-shrink-0" />
                        <span className="line-clamp-2">{task.message}</span>
                      </p>
                    )}
                    
                    {task.preferred_time && (
                      <p className="text-sm text-blue-600 mt-1">
                        ⏰ Preferred time: {task.preferred_time}
                      </p>
                    )}
                    
                    {task.notes && (
                      <p className="text-sm text-gray-500 mt-1 italic">
                        Notes: {task.notes}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 flex gap-2">
                    {task.status === 'pending' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateTask(task.task_id, { status: 'in_progress' })}
                        >
                          Start
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => updateTask(task.task_id, { status: 'completed' })}
                        >
                          Complete
                        </Button>
                      </>
                    )}
                    {task.status === 'in_progress' && (
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => updateTask(task.task_id, { status: 'completed' })}
                      >
                        Complete
                      </Button>
                    )}
                    {task.status === 'completed' && (
                      <Badge className="bg-green-100 text-green-800">✓ Done</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create New Task</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Task Type</label>
                <Select value={newTask.type} onValueChange={(v) => setNewTask({ ...newTask, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="callback">Callback Request</SelectItem>
                    <SelectItem value="whatsapp_followup">WhatsApp Follow-up</SelectItem>
                    <SelectItem value="scheduled_callback">Scheduled Callback</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Phone Number *</label>
                <Input 
                  placeholder="9876543210"
                  value={newTask.phone}
                  onChange={(e) => setNewTask({ ...newTask, phone: e.target.value })}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Customer Name</label>
                <Input 
                  placeholder="Customer name"
                  value={newTask.customer_name}
                  onChange={(e) => setNewTask({ ...newTask, customer_name: e.target.value })}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Notes/Message</label>
                <Input 
                  placeholder="Add notes..."
                  value={newTask.message}
                  onChange={(e) => setNewTask({ ...newTask, message: e.target.value })}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Priority</label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1" onClick={createTask} disabled={!newTask.phone}>
                  Create Task
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Task Detail Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Task Details</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3">
                {getStatusIcon(selectedTask.status)}
                <span className="font-medium text-lg">{getTypeLabel(selectedTask.type)}</span>
                <Badge className={getPriorityColor(selectedTask.priority)}>
                  {selectedTask.priority}
                </Badge>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Customer</p>
                  <p className="font-medium">{selectedTask.customer_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedTask.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium">{formatDate(selectedTask.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium capitalize">{selectedTask.status}</p>
                </div>
              </div>
              
              {selectedTask.message && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Message</p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedTask.message}</p>
                </div>
              )}
              
              {selectedTask.preferred_time && (
                <div className="text-blue-600 bg-blue-50 p-3 rounded-lg">
                  ⏰ Preferred time: {selectedTask.preferred_time}
                </div>
              )}
              
              {/* IVR/Missed Call Data */}
              {selectedTask.ivr_data && (
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-purple-700 mb-2">IVR Call Details</p>
                  <div className="text-sm space-y-1">
                    {selectedTask.ivr_data.call_duration && <p>Duration: {selectedTask.ivr_data.call_duration}s</p>}
                    {selectedTask.ivr_data.ivr_options_selected && <p>Options: {selectedTask.ivr_data.ivr_options_selected.join(' → ')}</p>}
                  </div>
                </div>
              )}
              
              {selectedTask.missed_call_data && (
                <div className="bg-orange-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-orange-700 mb-2">Missed Call Details</p>
                  <div className="text-sm space-y-1">
                    {selectedTask.missed_call_data.call_time && <p>Call Time: {selectedTask.missed_call_data.call_time}</p>}
                    {selectedTask.missed_call_data.virtual_number && <p>Virtual Number: {selectedTask.missed_call_data.virtual_number}</p>}
                  </div>
                </div>
              )}
              
              <Separator />
              
              <div className="flex gap-2">
                {selectedTask.status === 'pending' && (
                  <>
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        updateTask(selectedTask.task_id, { status: 'in_progress' });
                        setShowTaskModal(false);
                      }}
                    >
                      Start Task
                    </Button>
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        updateTask(selectedTask.task_id, { status: 'completed' });
                        setShowTaskModal(false);
                      }}
                    >
                      Mark Complete
                    </Button>
                  </>
                )}
                {selectedTask.status === 'in_progress' && (
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      updateTask(selectedTask.task_id, { status: 'completed' });
                      setShowTaskModal(false);
                    }}
                  >
                    Mark Complete
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => window.open(`tel:${selectedTask.phone}`, '_self')}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
}
